import {
  type ComponentProps,
  memo,
  type ReactElement,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { type BackgroundProps } from 'react-imgix';

import { type Clip, ClipStatus } from '@air/api/types';
import { ImgixImage } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { AssetGalleryCardFailedTranscode } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardFailedTranscode';
import { AssetGalleryCardProcessing } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardProcessing';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { useTransparentThumbnailPreview } from '~/hooks/useTransparentThumbnailPreview';
import { getClipImageAlt } from '~/utils/getClipImageAlt';
import { getRoundedImgixDimensions } from '~/utils/getRoundedImgixDimensions';
import {
  canRetryTransparentPreview,
  transparencyCheckerboardClass,
  transparentPreviewDisplaySrc,
  transparentPreviewImgixParams,
} from '~/utils/getTransparentPreviewSource';
import { isPreviewSrc } from '~/utils/isPreviewSrc';

export type GalleryAssetThumbnailImageProps = {
  /** URI for the image to be displayed in the clip thumbnail */
  src: string | undefined;
  /** URI for the animated image to be displayed on hover for a clip e.g an animated preview on video hover */
  animatedSrc?: string;
  /** width of the image that should be rendered in the gallery */
  width: number;
  /** height of the image that should be rendered in the gallery */
  height: number;
  /**
   * Imgix parameters
   */
  imgixParams?: BackgroundProps['imgixParams'];
  /** Called when the final CDN image has loaded (used to dismiss canvas import preview shimmer) */
  onFinalImageLoad?: () => void;
  /** Called with the browser-resolved currentSrc after the image loads — used to populate the drag preview cache */
  onSrcLoaded?: (currentSrc: string) => void;
  /** CloudFront (or other) URL used when the Imgix rendition fails */
  fallbackUrl?: string;
  /** Processed JPEG requested through Imgix when the original rendition fails */
  processedPreviewSrc?: string;
  /** Paint a checkerboard behind alpha and keep the Imgix output from flattening to JPEG */
  preserveTransparency?: boolean;
  /**
   * Source image size. Callers that omit it keep a transparent preview contained, since cropping a
   * logo is the failure we must avoid.
   */
  imageWidth?: number;
  imageHeight?: number;
} & Pick<Clip, 'status' | 'ext' | 'description'> &
  Pick<ComponentProps<'img'>, 'className'>;

export const GalleryAssetThumbnailImage = memo(
  ({
    animatedSrc,
    className,
    description,
    ext,
    fallbackUrl,
    height,
    imageHeight,
    imageWidth,
    imgixParams,
    onFinalImageLoad,
    preserveTransparency = false,
    processedPreviewSrc,
    onSrcLoaded,
    status,
    src,
    width,
  }: GalleryAssetThumbnailImageProps) => {
    const imageAlt = getClipImageAlt({ description }, 'empty');
    const [failedSrc, setFailedSrc] = useState<string | undefined>();
    const [isLoaded, setLoaded] = useState(false);
    const displayedSrc = transparentPreviewDisplaySrc({ failedSrc, processedPreviewSrc, src });
    const showCheckerboard = preserveTransparency && displayedSrc === src;
    const {
      thumbnailBoxRef,
      containedTransparency,
      inlineSvgPreviewUrl,
      markInlineSvgPreviewFailed,
      isAwaitingTransparentPreview,
    } = useTransparentThumbnailPreview({
      preserveTransparency,
      showCheckerboard,
      src,
      ext,
      animatedSrc,
      imageWidth,
      imageHeight,
    });

    const handleLoad = useCallback(
      (e?: SyntheticEvent<HTMLImageElement>) => {
        setLoaded(true);
        onFinalImageLoad?.();
        if (e?.currentTarget.currentSrc) {
          onSrcLoaded?.(e.currentTarget.currentSrc);
        }
      },
      [onFinalImageLoad, onSrcLoaded],
    );

    // The data URL is not cached for the drag ghost: it would hold the inlined SVG in memory and paint it
    // with `object-cover`, cropping logos. The ghost falls back to its own thumbnail instead.
    const handleInlineSvgPreviewLoad = useCallback(() => {
      setLoaded(true);
      onFinalImageLoad?.();
    }, [onFinalImageLoad]);

    const handleRenditionError = useCallback(() => {
      if (!canRetryTransparentPreview({ failedSrc, processedPreviewSrc, src })) {
        return false;
      }

      setFailedSrc(src);

      return true;
    }, [failedSrc, processedPreviewSrc, src]);

    const imgixDimensions = useMemo(() => getRoundedImgixDimensions({ height, width }), [height, width]);

    if ([ClipStatus.failed, ClipStatus.nonTranscodable].includes(status)) {
      return <AssetGalleryCardFailedTranscode ext={ext} />;
    }

    if (!src) {
      return <AssetGalleryCardProcessing ext={ext} />;
    }

    const isPreviewSource = isPreviewSrc(src);

    // The image radius reads the --gallery-card-inner-radius var (set by the public collection
    // page's Corner rounding setting — see shareLinkThumbnailRadius.ts) and falls back to the
    // native 4px, so every surface that doesn't set the var renders exactly as before.
    // Contained mode needs `size-auto`: with the width/height attributes plus preflight's
    // `height: auto`, max-w and max-h clamp each axis independently and distort the box, which
    // letterboxes the image and exposes the checkerboard. It trails `className` so a caller's
    // `size-full`/`w-full` can't win the tailwind-merge conflict.
    const imageClassNames: ComponentProps<'img'>['className'] = cn(
      'block rounded-[var(--gallery-card-inner-radius,4px)]',
      containedTransparency ? 'max-h-full max-w-full' : 'min-h-full w-full object-cover',
      isLoaded ? 'opacity-100' : 'opacity-0',
      transparencyCheckerboardClass(showCheckerboard),
      className,
      containedTransparency && 'size-auto object-contain',
    );

    let image: ReactElement | null;

    if (animatedSrc) {
      // biome-ignore lint/performance/noImgElement: animatedSrc is a video preview URL, not eligible for Next.js Image optimization
      image = <img alt={imageAlt} className={imageClassNames} src={animatedSrc} />;
    } else if (isPreviewSource) {
      // No opacity transition — the shimmer handles the loading state, image renders at full opacity immediately
      image = (
        // biome-ignore lint/performance/noImgElement: preview src is a blob/data/edits-bucket URL, not eligible for Next.js Image optimization
        <img
          alt={imageAlt}
          className={cn(
            'block rounded-[var(--gallery-card-inner-radius,4px)]',
            containedTransparency ? 'max-h-full max-w-full' : 'min-h-full w-full object-cover',
            transparencyCheckerboardClass(showCheckerboard),
            className,
            containedTransparency && 'size-auto object-contain',
          )}
          src={src}
        />
      );
    } else if (isAwaitingTransparentPreview) {
      image = null;
    } else if (inlineSvgPreviewUrl) {
      image = (
        // biome-ignore lint/performance/noImgElement: normalized SVG data URL is not an Imgix source
        <img
          alt={imageAlt}
          className={imageClassNames}
          onError={markInlineSvgPreviewFailed}
          onLoad={handleInlineSvgPreviewLoad}
          src={inlineSvgPreviewUrl}
        />
      );
    } else {
      image = (
        <ImgixImage
          alt={imageAlt}
          className={imageClassNames}
          fallbackUrl={fallbackUrl || getCloudFrontUrlFromImgixUrl(displayedSrc ?? '') || undefined}
          height={imgixDimensions.height}
          imgixParams={{
            cs: 'origin',
            fit: 'crop',
            ...imgixParams,
            ...transparentPreviewImgixParams(showCheckerboard),
            ...(containedTransparency && { fit: 'clip' }),
          }}
          onLoad={handleLoad}
          onRenditionError={handleRenditionError}
          src={displayedSrc ?? src}
          width={imgixDimensions.width}
        />
      );
    }

    if (isPreviewSource) {
      return (
        <div className="relative h-full w-full">
          {image}
          <div
            className={cn(
              'shimmer-background absolute inset-0 rounded-[var(--gallery-card-inner-radius,4px)] opacity-40',
            )}
          />
        </div>
      );
    }

    // The measured box must fill the thumbnail region in both contained and covered layouts.
    if (preserveTransparency) {
      return (
        <div
          className={cn('flex size-full', containedTransparency && 'items-center justify-center')}
          ref={thumbnailBoxRef}
        >
          {image}
        </div>
      );
    }

    return image;
  },
);

GalleryAssetThumbnailImage.displayName = 'GalleryAssetThumbnailImage';
